import React from "react";

import { Radio } from "../radio";
import {
  GroupContainer,
  GroupDescription,
  GroupText,
  OptionsWrapper,
} from "./styled";
import type { RadioGroupProps } from "./types";

export const RadioGroup: React.FC<RadioGroupProps> = ({
  name,
  value,
  defaultValue,
  onChange,
  options,
  direction = "vertical",
  size = "medium",
  disabled = false,
  label,
  description,
  labelPosition = "right",
  styleProps,
  className,
  testId,
}) => {
  const isControlled = typeof value === "string";
  const [internalValue, setInternalValue] = React.useState<string | undefined>(
    defaultValue,
  );
  const actualValue = isControlled ? (value as string) : internalValue;

  const groupName = React.useMemo(
    () => name ?? `radio-${Math.random().toString(36).slice(2)}`,
    [name],
  );

  const handleSelect = (nextValue: string) => {
    if (!isControlled) setInternalValue(nextValue);
    onChange?.(nextValue);
  };

  return (
    <GroupContainer
      className={className}
      $direction={direction}
      $disabled={disabled}
      $styleProps={styleProps}
      data-testid={testId}
      role="radiogroup"
    >
      {label && <GroupText $styleProps={styleProps}>{label}</GroupText>}
      {description && (
        <GroupDescription $styleProps={styleProps}>
          {description}
        </GroupDescription>
      )}

      <OptionsWrapper $direction={direction} $styleProps={styleProps}>
        {options.map((opt) => (
          <Radio
            key={opt.value}
            name={groupName}
            value={opt.value}
            label={opt.label}
            description={opt.description}
            size={size}
            disabled={disabled || opt.disabled}
            labelPosition={labelPosition}
            checked={actualValue === opt.value}
            onChange={(checked) => {
              if (checked) handleSelect(opt.value);
            }}
          />
        ))}
      </OptionsWrapper>
    </GroupContainer>
  );
};
